import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface CountUpProps {
  /** Target value to animate to (from 0). */
  value: number;
  /** Animation duration in ms (easeOutCubic). */
  durationMs?: number;
  /** Render the current (animating) value as a string. */
  format?: (v: number) => string;
}

/**
 * Count-up number, reduced-motion-safe. Renders `format(current)` and eases
 * from 0 to `value` over `durationMs`. If the user prefers reduced motion (or
 * we're in a non-DOM environment), it renders the final value immediately.
 */
export function CountUp({
  value,
  durationMs = 900,
  format = (v) => String(Math.round(v)),
}: CountUpProps): JSX.Element {
  const reduced = prefersReducedMotion();
  const [display, setDisplay] = useState<number>(reduced ? value : 0);
  const rafRef = useRef<number | undefined>(undefined);
  const startRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    startRef.current = undefined;
    const step = (ts: number): void => {
      if (startRef.current === undefined) startRef.current = ts;
      const progress = Math.min(1, (ts - startRef.current) / durationMs);
      setDisplay(value * easeOutCubic(progress));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [value, durationMs, reduced]);

  return <>{format(display)}</>;
}

export default CountUp;
